import { FormSubmissionError } from "../api/types";

/**
 * Extracts a user-friendly error message from the submission error
 */
export function getFormattedErrorMessage(
  error: FormSubmissionError | null,
): string | null {
  if (!error) return null;

  // Handle network errors
  if (error.name === "NetworkError") {
    return "Network error. Please check your internet connection and try again.";
  }

  // Handle API errors with specific response messages
  if (error.name === "ApiError" && error.response) {
    // First check for the message field in the response
    if (typeof error.response === "object" && error.response !== null) {
      const responseObj = error.response as {
        message?: string;
        error?: string;
        errors?: string[] | Array<{ message: string }>;
        status?: number;
        [key: string]: unknown;
      };

      // Check for message field first (preferred)
      if ("message" in responseObj && typeof responseObj.message === "string") {
        return responseObj.message;
      }

      // Fallback to error field if available
      if ("error" in responseObj && typeof responseObj.error === "string") {
        return responseObj.error;
      }

      // Check for errors array (some APIs return this format)
      if (
        "errors" in responseObj &&
        Array.isArray(responseObj.errors) &&
        responseObj.errors.length > 0
      ) {
        const firstError = responseObj.errors[0] as string | { message: string };
        if (typeof firstError === "string") {
          return firstError;
        } else if (
          typeof firstError === "object" &&
          firstError !== null &&
          "message" in firstError
        ) {
          return String((firstError as { message: string }).message);
        }
      }

      // Handle common HTTP status codes
      if ("status" in responseObj && typeof responseObj.status === "number") {
        const status = responseObj.status;
        if (status === 400) {
          return "There was a problem with your submission. Please check your information and try again.";
        }
        if (status === 403) {
          return "You don't have permission to complete this action.";
        }
        if (status === 404) {
          return "The requested resource was not found.";
        }
        if (status >= 500) {
          return "We're experiencing server issues. Please try again later.";
        }
      }
    }
  }

  // Fallback to the error's message property if available
  if (error.message) {
    return error.message;
  }

  // Ultimate fallback
  return "Something went wrong. Please try again.";
}
